import React, { useEffect, useRef, useState } from 'react';
import { PanelLeft, ChevronRight, ChevronDown, Clock, ShieldCheck, Check, User, LogOut } from 'lucide-react';
import { useMsal } from '@azure/msal-react';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import { Page } from '../../interfaces/PagesInterface';
import { findNavEntry, PAGES_WITHOUT_WEEK_PILL } from './navConfig';
import GlobalWeekPill from './GlobalWeekPill';
import GlobalEmployeeSearch from './GlobalEmployeeSearch';
import CompanyLogo from '../CompanyLogo';
import { canSwitchExperience, Experience } from '../../Pages/v2/employeePortal/portalSession';
import { getDefaultPageForLimitedUser, isLimitedUser } from '../../util/adminPermissions';

interface TopBarV2Props {
  onToggleCollapse: () => void;
  onSignOut: () => void;
  /** 'employee' = the Employee Time Portal shell: no employee search, no week pill (both call /admin). */
  variant?: 'admin' | 'employee';
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const TopBarV2: React.FC<TopBarV2Props> = ({ onToggleCollapse, onSignOut, variant = 'admin' }) => {
  const {
    page, setPage, bumpReload, userName, userRole, translator,
    hasAdminAccess, hasEmployeeAccess, experience, setExperience, selfRoleName,
  } = useSiteContext();
  const { instance, accounts } = useMsal();
  // The search results portal to <body>, so while they are up the bar has to outrank
  // its own backdrop or the field it came from goes dark and untypeable.
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const entry = findNavEntry(page);
  const crumbTitle = entry
    ? entry.item.label ?? translator.translate(entry.item.labelKey ?? entry.item.page)
    : '';

  const switchable = canSwitchExperience({
    adminAccess: hasAdminAccess,
    employeeAccess: hasEmployeeAccess,
    portalEnabled: !!config.employeePortal?.enabled,
  });
  const employeeShell = variant === 'employee';
  const roleLine = employeeShell ? selfRoleName ?? 'Employee' : userRole?.name || selfRoleName || '';
  const email = (instance.getActiveAccount() ?? accounts[0])?.username ?? '';

  // Close the menu on an outside click or Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const switchTo = (next: Experience) => {
    setMenuOpen(false);
    if (next === experience) return;
    setExperience(next);
    if (next === 'employee') setPage(Page.MyToday);
    else setPage(isLimitedUser(userRole) ? getDefaultPageForLimitedUser(userRole, config) : Page.Dashboard);
    bumpReload();
  };

  return (
    <div className={`topbar${searchOpen ? ' searching' : ''}`}>
      <div className="topbar-logo">
        <CompanyLogo src={config.logo} alt="Logo" />
      </div>

      <button type="button" className="collapse-btn" onClick={onToggleCollapse} title="Toggle menu">
        <PanelLeft className="collapse-btn-ic" size={18} />
      </button>

      {entry && (
        <div className="crumbs">
          {/* A group whose only entry shares its name (Reports › Reports) reads
              as a mistake — show the page on its own instead. */}
          {entry.group !== crumbTitle && (
            <>
              <span>{entry.group}</span>
              <ChevronRight className="crumbs-sep" size={14} />
            </>
          )}
          <b>{crumbTitle}</b>
        </div>
      )}

      <div className="grow" />

      {!employeeShell && (
        <>
          {/* setSearchOpen is passed as the setter itself, not an inline arrow — a fresh
              lambda each render would re-fire the effect that calls it. */}
          <GlobalEmployeeSearch onResultsOpenChange={setSearchOpen} />
          {!PAGES_WITHOUT_WEEK_PILL.has(page) && <GlobalWeekPill />}
        </>
      )}

      {switchable && (
        <div className="roleseg" role="tablist" aria-label="Experience">
          <button
            type="button"
            role="tab"
            aria-selected={experience === 'employee'}
            className={experience === 'employee' ? 'on' : ''}
            onClick={() => switchTo('employee')}
          >
            <Clock /> <span>Employee</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={experience === 'admin'}
            className={experience === 'admin' ? 'on' : ''}
            onClick={() => switchTo('admin')}
          >
            <ShieldCheck /> <span>Admin</span>
          </button>
        </div>
      )}

      <div className="mewrap" ref={menuRef}>
        <button
          type="button"
          className={`mebtn${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span className="txt">
            <span className="nm">{userName}</span>
            <span className="rl">{roleLine}</span>
          </span>
          <span className="av">{initialsOf(userName)}</span>
          <ChevronDown className="chev" />
        </button>
        {menuOpen && (
          <div className="rolemenu" role="menu">
            <div className="rm-lab">Signed in as</div>
            <div className="rm-row static">
              <span className="rm-ic"><User /></span>
              <span className="rm-tx">
                <span className="rm-t">{userName}</span>
                <span className="rm-s">{[selfRoleName ?? (employeeShell ? null : userRole?.name), email].filter(Boolean).join(' · ')}</span>
              </span>
            </div>
            {switchable && (
              <>
                <div className="rm-div" />
                <div className="rm-lab">Switch experience</div>
                <button type="button" role="menuitem" className={`rm-row${experience === 'employee' ? ' cur' : ''}`} onClick={() => switchTo('employee')}>
                  <span className="rm-ic"><Clock /></span>
                  <span className="rm-tx">
                    <span className="rm-t">Employee</span>
                    <span className="rm-s">My time, timesheet &amp; time off</span>
                  </span>
                  {experience === 'employee' && <span className="rm-chk"><Check /></span>}
                </button>
                <button type="button" role="menuitem" className={`rm-row${experience === 'admin' ? ' cur' : ''}`} onClick={() => switchTo('admin')}>
                  <span className="rm-ic"><ShieldCheck /></span>
                  <span className="rm-tx">
                    <span className="rm-t">Admin</span>
                    <span className="rm-s">Payroll, review &amp; approvals</span>
                  </span>
                  {experience === 'admin' && <span className="rm-chk"><Check /></span>}
                </button>
              </>
            )}
            <div className="rm-div" />
            <button type="button" role="menuitem" className="rm-row" onClick={() => { setMenuOpen(false); onSignOut(); }}>
              <span className="rm-ic"><LogOut /></span>
              <span className="rm-tx"><span className="rm-t">{translator.translate('SignOut')}</span></span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBarV2;
